import {
  CSSProperties,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';
import { useFeatureFlag } from 'featureFlags';

import {
  firstGridTableInView,
  subscribeGridVisibility
} from 'components/MultiTableGrid/gridScrollChannel';
import Flame from 'components/Streaks/Flame';
import { flameStageFor } from 'components/Streaks/model/milestones';
import {
  streakStore,
  useStreakSnapshot,
  useStreakStore
} from 'components/Streaks/state/streakStore';
import { useStatusAvatars } from 'components/Streaks/state/useStatusAvatars';
import { useStreakReducedMotion } from 'components/Streaks/useStreakReducedMotion';

import {
  BOARD_SWEEP_MS,
  BURST_MS,
  CHIP_CLAIM_WINDOW_MS,
  CHIP_MS,
  claimBeat,
  DEAL_FADE_MS,
  dropMoment,
  feltKey,
  GRID_IN_VIEW_FRACTION,
  isFeltOnScreen,
  markFeltOnScreen,
  momentKey,
  MomentPlan,
  NEXT_HAND_WAIT_MS,
  noteMoment,
  planMoment,
  releaseBeat,
  subscribeFeltsOnScreen
} from './momentPlan';
import RisingFlames, { FlameTuning } from './RisingFlames';

import styles from './StreakTableMoment.module.scss';

const NUM_GLOW = [
  '',
  'rgba(255,120,40,0.65)',
  'rgba(255,150,40,0.65)',
  'rgba(255,200,80,0.65)',
  'rgba(255,230,160,0.7)',
  'rgba(170,210,255,0.7)',
  'rgba(110,160,255,0.75)'
];

export interface StreakTableMomentProps {
  /** `texas` or `omaha`; table ids repeat across the two. */
  gameType: string | undefined;
  gameID: string | number | undefined;
  /** The hand number on the felt; a new one means the next deal has started. */
  gameNo: string | number | undefined;
  /** Non-null while a pot's winner is declared (Game.tsx's confetti key). */
  winnerDeclaredKey: string | null;
  /** The dealer seat; it changes as the next hand begins and the button moves. */
  dealerSeat?: string | number;
  /** `table`: the single-table view (a phone, or its tiles). `grid`: a desktop grid cell. */
  surface: 'table' | 'grid';
  /** This felt's surface is the one on screen (its view is showing and its panel isn't parked). */
  visible: boolean;
  /** The table the player is on in the single-table view; the chip goes there. */
  focused: boolean;
  /** The player is free here: watching, folded, sitting out or between hands (isFreeAtTable). */
  free: boolean;
  /** The player has a seat here, so the next deal reaches them. */
  seated: boolean;
  /** How long beat two lasts, in place of BURST_MS: the demo table's timing control only. */
  burstMs?: number;
  /** The demo table's flame controls only (RisingFlames FlameTuning). */
  flameTuning?: FlameTuning;
}

/** Whether this grid cell is in view, and whether it is the first one that is. */
function useGridPlace(gameID: string | null) {
  const [place, setPlace] = useState({ inView: false, first: false });
  useEffect(() => {
    if (!gameID) {
      setPlace({ inView: false, first: false });
      return undefined;
    }
    // The channel fires every scroll frame; state changes only when the answer does.
    return subscribeGridVisibility((fractions) => {
      const inView = (fractions[gameID] ?? 0) >= GRID_IN_VIEW_FRACTION;
      const first = inView && firstGridTableInView(GRID_IN_VIEW_FRACTION) === gameID;
      setPlace((p) => (p.inView === inView && p.first === first ? p : { inView, first }));
    });
  }, [gameID]);
  return place;
}

type Burst = MomentPlan & {
  /** Started mid-break at a seat: the next deal cuts it short (AC 5). */
  cutOnDeal: boolean;
  gameNo: string | number | undefined;
};

/**
 * The at-table streak moment (EN-11163). One is owed per qualifying day per
 * player, and the felts on screen share it (momentPlan's claims): it never
 * plays once per table, never off screen, never twice.
 *
 * Beat one, the chip, at the qualifying flop: on the table the player is on
 * (the first table in view on the desktop grid), only while they are in a hand
 * there; a felt they are free at goes straight to beat two (5M). The 100K chip
 * steps aside while it shows.
 *
 * Beat two, flames and the count flip, on the first free felt on screen: one
 * the player is watching, folded at, sitting out at or between hands at plays
 * it right away; a felt where the winner is shown plays it as the next hand
 * begins (the dealer button moving), so it never covers the winner animation,
 * or NEXT_HAND_WAIT_MS later if no next hand comes. A felt waiting like that
 * gives the moment back if it leaves the screen. On the desktop grid the
 * flames stay on that felt, off the tables beside it (JRam 11/03). Leaving
 * for the lobby first drops it
 * (useDropMomentInLobby).
 */
export function StreakTableMomentView({
  enabled,
  gameType,
  gameID,
  gameNo,
  winnerDeclaredKey,
  dealerSeat,
  surface,
  visible,
  focused,
  free,
  seated,
  burstMs,
  flameTuning
}: StreakTableMomentProps & { enabled: boolean }) {
  const snapshot = useStreakSnapshot();
  const still = useStreakReducedMotion();
  const statusAvatars = useStatusAvatars(enabled);
  const chipTableId = useStreakStore((s) => s.chipTableId);
  const plan = useMemo(
    () => (enabled ? planMoment(snapshot, gameType, gameID, statusAvatars) : null),
    [enabled, snapshot, gameType, gameID, statusAvatars]
  );
  const grid = useGridPlace(surface === 'grid' && visible && gameID ? String(gameID) : null);
  const onScreen = visible && (surface === 'table' || grid.inView);
  // Before any felt's effects run, so none claims a beat that belongs to a
  // counting table mounting in the same commit.
  useLayoutEffect(
    () =>
      onScreen && gameID !== undefined ? markFeltOnScreen(feltKey(gameType, gameID)) : undefined,
    [onScreen, gameType, gameID]
  );
  const countedKey = snapshot?.moment
    ? feltKey(snapshot.moment.gameType, snapshot.moment.gameId)
    : null;
  // The table where the day counted is on screen: the moment plays there only.
  const countedOnScreen = useSyncExternalStore(subscribeFeltsOnScreen, () =>
    countedKey ? isFeltOnScreen(countedKey) : false
  );
  const elsewhere = !!plan && !plan.countedHere && countedOnScreen;
  const chipHere = elsewhere
    ? false
    : surface === 'table'
    ? onScreen && focused
    : grid.first || (!!plan?.countedHere && grid.inView);
  const tableId = String(gameID);

  const [chip, setChip] = useState<MomentPlan | null>(null);
  const [burst, setBurst] = useState<Burst | null>(null);
  const [fading, setFading] = useState(false);
  // Beat two claimed here, waiting for the next hand to begin.
  const pendingRef = useRef<{
    plan: MomentPlan;
    gameNo: typeof gameNo;
    dealerSeat: typeof dealerSeat;
  } | null>(null);
  const fallbackRef = useRef<number | null>(null);

  const clearFallback = () => {
    if (fallbackRef.current !== null) window.clearTimeout(fallbackRef.current);
    fallbackRef.current = null;
  };
  const play = (p: MomentPlan, cutOnDeal: boolean) => {
    clearFallback();
    pendingRef.current = null;
    setFading(false);
    // The chip runs long (CHIP_MS); a short hand can bring beat two first.
    setChip(null);
    if (streakStore.getState().chipTableId === tableId) streakStore.setChipTable(null);
    setBurst({ ...p, cutOnDeal, gameNo });
  };
  const playPending = () => {
    if (pendingRef.current) play(pendingRef.current.plan, false);
  };

  useEffect(() => clearFallback, []);
  useEffect(() => {
    if (plan) noteMoment(plan.key);
  }, [plan?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Beat one: the chip, on the table the player is on, while they are in a hand there.
  useEffect(() => {
    if (!plan || !plan.chip || !chipHere || free) return undefined;
    if (Date.now() - noteMoment(plan.key) > CHIP_CLAIM_WINDOW_MS) return undefined;
    if (!claimBeat(plan.key, 'chip')) return undefined;
    setChip(plan);
    streakStore.setChipTable(tableId);
    const t = window.setTimeout(() => {
      setChip(null);
      streakStore.setChipTable(null);
    }, CHIP_MS);
    return () => {
      window.clearTimeout(t);
      setChip(null);
      streakStore.setChipTable(null);
    };
    // Claimed at the moment the plan lands; free is read then, not tracked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.key, chipHere]);

  // Beat two: the first free felt on screen claims it.
  useEffect(() => {
    if (!plan) return;
    const pending = pendingRef.current;
    if (pending) {
      // Waiting here, then the player looked away: give it back (AC 6).
      if (!onScreen) {
        pendingRef.current = null;
        clearFallback();
        releaseBeat(pending.plan.key, 'burst');
      }
      return;
    }
    if (!onScreen || burst || elsewhere) return;
    // A pre-flop win: its winner is already on the felt where it counted.
    const winnerShown = winnerDeclaredKey !== null || (plan.countedHere && !plan.chip);
    // Right away waits for the chip to go; the next hand comes after it anyway.
    if (free ? chipTableId !== null : !winnerShown) return;
    if (!claimBeat(plan.key, 'burst')) return;
    if (free) {
      play(plan, seated);
      return;
    }
    pendingRef.current = { plan, gameNo, dealerSeat };
    fallbackRef.current = window.setTimeout(playPending, NEXT_HAND_WAIT_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.key, onScreen, free, winnerDeclaredKey, chipTableId, elsewhere]);

  // The next hand begins: the dealer button moves (or the hand number changes).
  // After the winner animation and once the old board has swept off the felt
  // (BOARD_SWEEP_MS), before the new cards land.
  useEffect(() => {
    const pending = pendingRef.current;
    if (pending && (gameNo !== pending.gameNo || dealerSeat !== pending.dealerSeat)) {
      clearFallback();
      fallbackRef.current = window.setTimeout(playPending, BOARD_SWEEP_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameNo, dealerSeat]);

  // A burst that started mid-break at a seat fades as the next deal reaches it.
  useEffect(() => {
    if (burst?.cutOnDeal && gameNo !== burst.gameNo) setFading(true);
  }, [gameNo, burst]);

  // The badge takes the felt's centre while it shows (EN-11386): the board,
  // pot, felt text and winner message step aside, as the 100K chip does for the chip.
  const bursting = burst !== null;
  useEffect(() => {
    if (!bursting) return undefined;
    streakStore.setBurstTable(tableId);
    return () => {
      if (streakStore.getState().burstTableId === tableId) streakStore.setBurstTable(null);
    };
  }, [bursting, tableId]);

  const burstLength = burst ? burstMs ?? BURST_MS[burst.burst] : 0;
  // Beat two plays its full length, or DEAL_FADE_MS once cut.
  useEffect(() => {
    if (!burst) return undefined;
    const t = window.setTimeout(
      () => {
        setBurst(null);
        setFading(false);
      },
      fading ? DEAL_FADE_MS : burstLength
    );
    return () => window.clearTimeout(t);
  }, [burst, fading, burstLength]);

  const stage = snapshot ? flameStageFor(snapshot.milestones, snapshot.currentStreak) : 1;

  const flames = burst ? (
    <RisingFlames durationMs={burstLength} kind={burst.burst} stage={stage} tuning={flameTuning} />
  ) : null;

  return (
    <>
      {chip && (
        <div className={`${styles.chip} ${still ? styles.still : ''}`} aria-hidden='true'>
          <Flame stage={stage || 1} size={30} />
          <span className={styles.chipText}>
            <span className={styles.chipDay}>Day {chip.days}</span>
            <span className={styles.chipCounted}>Counted</span>
          </span>
        </div>
      )}
      {burst && (
        <div
          className={`${styles.burst} ${still ? styles.still : ''} ${fading ? styles.fading : ''}`}
          style={
            {
              '--burst-ms': `${burstLength}ms`,
              '--num-glow': NUM_GLOW[stage]
            } as CSSProperties
          }
        >
          {!still && flames}
          <div className={styles.badge} aria-hidden='true'>
            <span className={styles.vignette} />
            <span className={styles.medallion}>
              <Flame stage={stage || 1} size={86} flare={!still} />
            </span>
            <span className={styles.count}>
              <span className={styles.countOld}>{Math.max(0, burst.days - 1)}</span>
              <span className={styles.countNew}>{burst.days}</span>
            </span>
            <span className={styles.label}>Day streak</span>
            {burst.milestoneName && (
              <span className={styles.ribbon}>{burst.milestoneName} status</span>
            )}
            {burst.rewardLine && <span className={styles.reward}>{burst.rewardLine}</span>}
            {burst.burst === 'first' && (
              <span className={styles.firstLine}>
                Streak started, with a free freeze in your pocket.
              </span>
            )}
          </div>
          {/* Heard once (AC 6). */}
          <span className={styles.srOnly} role='status'>
            {burst.milestoneName
              ? `${burst.milestoneName} status unlocked`
              : `Day ${burst.days} counted`}
          </span>
        </div>
      )}
    </>
  );
}

/**
 * Leaving for the lobby before beat two drops the owed moment (AC 5); the
 * banner shows the new count instead. Mounted once, above the views.
 */
export function useDropMomentInLobby(inLobby: boolean) {
  const snapshot = useStreakSnapshot();
  const key = momentKey(snapshot);
  useEffect(() => {
    if (inLobby && key) dropMoment(key);
  }, [inLobby, key]);
}

/** The table moment behind the ops.streaks flag. */
export default function StreakTableMoment(props: StreakTableMomentProps) {
  const enabled = useFeatureFlag('streaks');
  return <StreakTableMomentView {...props} enabled={enabled} />;
}
